<template lang="pug">
div
  v-alert.mb-3(v-for='item in createdUsers' :key='`created-${item.email}`' type='success' dense outlined)
    span {{$t('TENANT_PIPELINE.ADMIN_CREATED')}}：{{ item.last_name }}{{ item.first_name }}（{{ item.email }}）
  v-form(ref='form' v-model='valid')
    v-card.mb-3.pa-3(v-for='(row, index) in rows' :key='row.uid' outlined)
      .d-flex.align-center.mb-2
        span(style='color:#444444; font-weight: 700; font-size: 14px;') {{$t('TENANT_PIPELINE.ADMIN')}} {{ index + 1 }}
        v-spacer
        v-btn(icon small :disabled='busy' @click='removeRow(index)' :title=`$t('GENERAL.DELETE')`)
          v-icon(size='18') mdi-delete
      v-alert.mb-2(v-if='row.error' type='error' dense text) {{ row.error }}
      v-row(no-gutters)
        v-col.pr-2(cols='6')
          v-text-field(v-model='row.last_name' :label=`$t('ADD_SUPERUSER.LASTNAME')` outlined dense :rules='formRule.lastNameRules' :disabled='busy' @input='emitStatus')
        v-col.pl-2(cols='6')
          v-text-field(v-model='row.first_name' :label=`$t('ADD_SUPERUSER.FIRSTNAME')` outlined dense :rules='formRule.firstNameRules' :disabled='busy' @input='emitStatus')
        v-col(cols='12')
          v-text-field(v-model='row.email' :label=`$t('ADD_SUPERUSER.EMAIL')` type='email' outlined dense :rules='formRule.emailRules' :disabled='busy' @input='emitStatus')
        v-col(cols='12')
          v-text-field(
            v-model='row.password'
            :label=`$t('ADD_SUPERUSER.PASSWORD')`
            :type='row.showPassword ? "text" : "password"'
            :append-icon='row.showPassword ? "fa fa-eye" : "fa fa-eye-slash"'
            @click:append='row.showPassword = !row.showPassword'
            outlined
            dense
            :rules='formRule.passwordRules'
            :hint=`$t('ADD_SUPERUSER.PASSWORD__HINT')`
            persistent-hint
            :disabled='busy'
            @input='emitStatus'
          )
            template(v-slot:append-outer)
              v-btn.mt-n1(small depressed :disabled='busy' @click='generatePassword(row)') {{$t('TENANT_PIPELINE.GENERATE_PASSWORD')}}
  v-btn(:ripple='false' outlined color='#18bedf' :disabled='busy' @click='addRow')
    v-icon.mr-1(size='12') icon-add
    span {{$t('TENANT_PIPELINE.ADD_ADMIN')}}
</template>

<script>
import Vue from "vue";
import i18n from '/common/plugins/vue-i18n.js';
import TenantCenterService from "@/services/TenantCenterService";
import { errorText } from "@/components/TenantCenter/Pipeline/pipelineUtils";

// 與既有「新增全域管理員」對話框相同的密碼規則
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&])[A-Za-z\d$@$!%*?&]{8,30}/;
const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const PASSWORD_CHARSETS = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnopqrstuvwxyz', '23456789', '$@!%*?&'];
const PASSWORD_LENGTH = 16;

let uidSeed = 0;

function randomInt(max) {
  const buffer = new Uint32Array(1);
  window.crypto.getRandomValues(buffer);
  return buffer[0] % max;
}

function rowValid(row) {
  return !!row.last_name && row.last_name.length <= 50
    && !!row.first_name && row.first_name.length <= 50
    && EMAIL_PATTERN.test(row.email || '') && row.email.length <= 50
    && PASSWORD_PATTERN.test(row.password || '');
}

export default Vue.extend({
  props: {
    tenant: { type: Object, require: true },
    busy: { type: Boolean, default: false },
  },
  data() {
    return {
      rows: [],
      createdUsers: [],
      valid: true,
      formRule: {
        lastNameRules: [
          (v) => !!v || i18n.t('RULE.RULE_R_LASTNAME_3'),
          (v) => /^.{0,50}$/.test(v) || i18n.t('RULE.RULE_50'),
        ],
        firstNameRules: [
          (v) => !!v || i18n.t('RULE.RULE_R_FIRSTNAME'),
          (v) => /^.{0,50}$/.test(v) || i18n.t('RULE.RULE_50'),
        ],
        emailRules: [
          (v) => !!v || i18n.t('RULE.RULE_R'),
          (v) => EMAIL_PATTERN.test(v) || i18n.t('RULE.RULE_EMAIL'),
          (v) => /^.{0,50}$/.test(v) || i18n.t('RULE.RULE_50'),
        ],
        passwordRules: [
          (v) => !!v || i18n.t('RULE.RULE_R'),
          (v) => PASSWORD_PATTERN.test(v) || i18n.t('ADD_SUPERUSER.PASSWORD__HINT'),
        ],
      },
    };
  },
  mounted() {
    this.emitStatus();
  },
  methods: {
    emitStatus() {
      this.$nextTick(() => {
        const dirty = this.rows.length > 0;
        this.$emit('status', { dirty: dirty, ready: !dirty || this.rows.every(rowValid) });
      });
    },
    addRow() {
      uidSeed += 1;
      this.rows.push({ uid: uidSeed, last_name: '', first_name: '', email: '', password: '', showPassword: false, error: '' });
      this.emitStatus();
    },
    removeRow(index) {
      this.rows.splice(index, 1);
      this.emitStatus();
    },
    generatePassword(row) {
      // 每類字元至少一個，其餘從全部字元中取，最後打散
      const all = PASSWORD_CHARSETS.join('');
      const chars = PASSWORD_CHARSETS.map((set) => set[randomInt(set.length)]);
      while (chars.length < PASSWORD_LENGTH) {
        chars.push(all[randomInt(all.length)]);
      }
      for (let i = chars.length - 1; i > 0; i--) {
        const j = randomInt(i + 1);
        [chars[i], chars[j]] = [chars[j], chars[i]];
      }
      row.password = chars.join('');
      row.showPassword = true;
      this.emitStatus();
    },
    async submit() {
      if (!this.$refs.form.validate()) {
        throw new Error(i18n.t('TENANT_PIPELINE.FORM_INVALID'));
      }
      const total = this.rows.length;
      let created = 0;
      const failed = [];
      this.$emit('progress', `${i18n.t('TENANT_PIPELINE.CREATING_ACCOUNT')}(${created}/${total})`);
      // 依序逐一建立：授權數檢查與信箱重複檢查都依賴前一位已寫入
      for (const row of this.rows) {
        row.error = '';
        try {
          await TenantCenterService.pipelineCreateSuperUser({
            schema_name: this.tenant.schema_name,
            username: row.email,
            email: row.email,
            password: row.password,
            first_name: row.first_name,
            last_name: row.last_name,
            createUpchat: false,
          });
          created += 1;
          this.createdUsers.push({ last_name: row.last_name, first_name: row.first_name, email: row.email });
        } catch (error) {
          row.error = errorText(error);
          failed.push(row);
        }
        this.$emit('progress', `${i18n.t('TENANT_PIPELINE.CREATING_ACCOUNT')}(${created}/${total})`);
      }
      this.rows = failed;
      this.emitStatus();
      if (failed.length) {
        throw new Error(i18n.t('TENANT_PIPELINE.ADMIN_PARTIAL_FAILED', { failed: failed.length, total: total }));
      }
    },
  },
});
</script>
